import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { SharedModule } from 'primeng/api';
import { AttentionScheduleConfig, UpdateAttentionSchedulePayload } from 'src/app/models/attention-schedule.interface';

const AWAY_MESSAGE_MAX_LENGTH = 500;
// Debe coincidir con _DEFAULT_ATTENTION_SCHEDULE_START/END en bp_chatbot_integrations.py:
// ver is_within_schedule() en app/utils/date.py.
const DEFAULT_START_TIME = '00:00';
const DEFAULT_END_TIME = '00:00';

@Component({
  selector: 'globot-attention-schedule-config',
  standalone: true,
  imports: [CommonModule, FormsModule, CalendarModule, InputSwitchModule, InputTextareaModule, SharedModule],
  templateUrl: './attention-schedule-config.component.html',
  styleUrls: ['./attention-schedule-config.component.scss'],
})
export class AttentionScheduleConfigComponent implements OnChanges {
  /** Auto-guarda cada campo apenas cambia (sin botón "Guardar"), según diseño instagram-v2.pen. */
  @Input() config: AttentionScheduleConfig | null = null;
  @Input() saving = false;
  /** Nota específica de canal bajo la zona horaria (p.ej. la aclaración de comentarios en Instagram). Oculta si es null. */
  @Input() channelNote: string | null = null;
  @Output() save = new EventEmitter<UpdateAttentionSchedulePayload>();

  readonly awayMessageMaxLength = AWAY_MESSAGE_MAX_LENGTH;

  scheduleEnabled = false;
  startTime: Date | null = null;
  endTime: Date | null = null;
  awayMessageEnabled = false;
  awayMessageText = '';
  private awayMessagePending = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && this.config) {
      this.scheduleEnabled = !!this.config.attention_schedule_enabled;
      this.startTime = this.toDate(this.config.attention_schedule_start_time);
      this.endTime = this.toDate(this.config.attention_schedule_end_time);
      if (!this.awayMessagePending) {
        this.awayMessageEnabled = !!this.config.away_message_enabled;
        this.awayMessageText = this.config.away_message_text ?? '';
      }
    }
  }

  onScheduleToggle(enabled: boolean): void {
    this.scheduleEnabled = enabled;
    if (!enabled) {
      this.save.emit({ attention_schedule_enabled: false });
      return;
    }
    // Activar sin horas propias todavía: se completan con el mismo default
    // (nunca se llama al servicio mientras el horario sigue apagado).
    const payload: UpdateAttentionSchedulePayload = { attention_schedule_enabled: true };
    if (!this.startTime) {
      this.startTime = this.toDate(DEFAULT_START_TIME);
      payload.attention_schedule_start_time = DEFAULT_START_TIME;
    }
    if (!this.endTime) {
      this.endTime = this.toDate(DEFAULT_END_TIME);
      payload.attention_schedule_end_time = DEFAULT_END_TIME;
    }
    this.save.emit(payload);
  }

  onStartTimeClose(): void {
    const value = this.toTimeString(this.startTime);
    if (value === (this.config?.attention_schedule_start_time ?? null)) {
      return;
    }
    this.save.emit({ attention_schedule_start_time: value });
  }

  onEndTimeClose(): void {
    const value = this.toTimeString(this.endTime);
    if (value === (this.config?.attention_schedule_end_time ?? null)) {
      return;
    }
    this.save.emit({ attention_schedule_end_time: value });
  }

  onAwayMessageToggle(enabled: boolean): void {
    this.awayMessageEnabled = enabled;
    if (!enabled) {
      this.awayMessagePending = false;
      this.save.emit({ away_message_enabled: false });
      return;
    }
    if (this.awayMessageText.trim()) {
      this.save.emit({ away_message_enabled: true });
    } else {
      this.awayMessagePending = true;
    }
  }

  onAwayMessageBlur(): void {
    const value = this.awayMessageText.trim() || null;
    const textChanged = value !== (this.config?.away_message_text ?? null);
    const enabledChanged = this.awayMessageEnabled !== !!this.config?.away_message_enabled;
    this.awayMessagePending = false;
    if (!textChanged && !enabledChanged) {
      return;
    }
    const payload: UpdateAttentionSchedulePayload = {};
    if (textChanged) {
      payload.away_message_text = value;
    }
    if (enabledChanged) {
      payload.away_message_enabled = this.awayMessageEnabled;
    }
    this.save.emit(payload);
  }

  private toDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const [hours, minutes] = value.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  private toTimeString(value: Date | null): string | null {
    if (!value) {
      return null;
    }
    const hh = value.getHours().toString().padStart(2, '0');
    const mm = value.getMinutes().toString().padStart(2, '0');
    return `${hh}:${mm}`;
  }
}
